import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Mail, Lock, Loader2, ArrowLeft, ExternalLink } from "lucide-react";
import { base44 } from "@/api/base44Client";
import { useAuth } from "@/lib/AuthContext";

export default function ClientPortalLogin() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const { checkUserAuth } = useAuth();

  const handleSubmit = async (event) => {
    event.preventDefault();
    setError("");
    setLoading(true);

    try {
      await base44.auth.loginViaEmailPassword(email, password);
      await checkUserAuth();
      navigate("/", { replace: true });
    } catch (err) {
      setError(err?.message || "Unable to log in. Please check your email and password.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#f5f5f3] text-slate-900 flex flex-col">
      <header className="border-b border-slate-200 bg-white">
        <div className="mx-auto flex min-h-16 w-full max-w-6xl items-center justify-between px-5 sm:px-8">
          <a
            href="https://www.2dudestech.com.au/pages/drill-log-pro"
            className="flex items-center gap-3"
            target="_blank"
            rel="noreferrer"
          >
            <div className="flex h-9 w-9 items-center justify-center rounded-md bg-slate-900 text-xs font-black tracking-tight text-white">
              2D
            </div>
            <div>
              <div className="text-sm font-bold leading-none tracking-wide">2 DUDES TECH</div>
              <div className="mt-1 text-[10px] font-medium uppercase tracking-[0.18em] text-slate-500">Technology &amp; Development</div>
            </div>
          </a>

          <a
            href="https://www.2dudestech.com.au/pages/drill-log-pro"
            className="hidden items-center gap-1.5 text-sm font-medium text-slate-600 transition hover:text-slate-950 sm:flex"
            target="_blank"
            rel="noreferrer"
          >
            Drill Log Pro
            <ExternalLink className="h-3.5 w-3.5" />
          </a>
        </div>
      </header>

      <main className="flex flex-1 items-center justify-center px-5 py-12 sm:px-8">
        <section className="w-full max-w-md">
          <div className="mb-7 text-center">
            <div className="mb-4 inline-flex rounded-lg bg-slate-900 px-3 py-2 text-xs font-bold uppercase tracking-[0.16em] text-white">
              Drill Log Pro
            </div>
            <h1 className="text-3xl font-bold tracking-tight text-slate-950">Client Portal</h1>
            <p className="mt-2 text-sm leading-6 text-slate-600">
              Sign in to access your Drill Log Pro account.
            </p>
          </div>

          <div className="rounded-xl border border-slate-200 bg-white p-6 shadow-sm sm:p-8">
            {error && (
              <div className="mb-5 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700" role="alert">
                {error}
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-5">
              <div>
                <label htmlFor="client-portal-email" className="mb-2 block text-sm font-semibold text-slate-800">
                  Email
                </label>
                <div className="relative">
                  <Mail className="pointer-events-none absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" aria-hidden="true" />
                  <input
                    id="client-portal-email"
                    name="email"
                    type="email"
                    autoComplete="email"
                    value={email}
                    onChange={(event) => setEmail(event.target.value)}
                    placeholder="you@example.com"
                    className="h-12 w-full rounded-lg border border-slate-300 bg-white pl-10 pr-3 text-sm outline-none transition placeholder:text-slate-400 focus:border-slate-700 focus:ring-2 focus:ring-slate-200"
                    required
                  />
                </div>
              </div>

              <div>
                <div className="mb-2 flex items-center justify-between gap-4">
                  <label htmlFor="client-portal-password" className="block text-sm font-semibold text-slate-800">
                    Password
                  </label>
                  <Link to="/forgot-password" className="text-xs font-semibold text-slate-600 hover:text-slate-950 hover:underline">
                    Forgot password?
                  </Link>
                </div>
                <div className="relative">
                  <Lock className="pointer-events-none absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" aria-hidden="true" />
                  <input
                    id="client-portal-password"
                    name="password"
                    type="password"
                    autoComplete="current-password"
                    value={password}
                    onChange={(event) => setPassword(event.target.value)}
                    placeholder="Enter your password"
                    className="h-12 w-full rounded-lg border border-slate-300 bg-white pl-10 pr-3 text-sm outline-none transition placeholder:text-slate-400 focus:border-slate-700 focus:ring-2 focus:ring-slate-200"
                    required
                  />
                </div>
              </div>

              <button
                type="submit"
                disabled={loading}
                className="flex h-12 w-full items-center justify-center rounded-lg bg-slate-900 px-4 text-sm font-semibold text-white transition hover:bg-slate-800 disabled:cursor-not-allowed disabled:opacity-60"
              >
                {loading ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Signing in...
                  </>
                ) : (
                  "Sign in"
                )}
              </button>
            </form>

            <div className="mt-6 border-t border-slate-100 pt-5 text-center">
              <p className="text-xs leading-5 text-slate-500">
                Need access to Drill Log Pro? Contact 2 Dudes Tech to set up your account.
              </p>
            </div>
          </div>

          <div className="mt-6 flex items-center justify-center">
            <a
              href="https://www.2dudestech.com.au/pages/drill-log-pro"
              className="inline-flex items-center gap-2 text-sm font-medium text-slate-600 hover:text-slate-950"
            >
              <ArrowLeft className="h-4 w-4" />
              Back to Drill Log Pro
            </a>
          </div>
        </section>
      </main>

      <footer className="border-t border-slate-200 bg-white">
        <div className="mx-auto flex w-full max-w-6xl flex-col gap-2 px-5 py-5 text-center text-xs text-slate-500 sm:flex-row sm:items-center sm:justify-between sm:px-8 sm:text-left">
          <div>
            <span>© 2026 2 Dudes Tech Pty Ltd. All rights reserved.</span>
            <span className="ml-2">Drill Log Pro Client Portal — Test Phase</span>
          </div>
          <div className="flex justify-center gap-3 sm:justify-end">
            <a href="/legal#privacy" className="hover:underline">Privacy Policy</a>
            <a href="/legal#terms" className="hover:underline">Terms of Service</a>
            <a href="/tools/coming-soon-support" className="hover:underline">Support</a>
          </div>
        </div>
      </footer>
    </div>
  );
}
